import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { CaretRight } from '@phosphor-icons/react';
import { useShowcaseText } from '../../i18n/useShowcaseText';
import './Breadcrumbs.css';

/**
 * Breadcrumbs — путь до текущей страницы (#1330).
 *
 * ## Зачем вместо ссылки «назад»
 *
 * Ссылка со стрелкой отвечает на вопрос «как уйти», крошки — на вопрос «где я».
 * На карточке машины это разные вещи: покупатель попал сюда из каталога, из
 * поиска или по прямой ссылке из мессенджера, и в последнем случае «назад»
 * вести некуда — истории нет, а место в разделе есть всегда.
 *
 * ## Как устроено
 *
 * - **Последний элемент — не ссылка.** Он и есть текущая страница; ссылка на
 *   саму себя ничего не делает и сбивает с толку. Помечен `aria-current="page"`.
 * - **Разделители нарисованы CSS-псевдоэлементом**, а не текстом в разметке:
 *   читалка иначе диктует «Catalog больше Kia Picanto».
 * - **Обёртка — `<nav aria-label>`**: это навигация, и читалке нужно имя,
 *   чтобы отличить её от меню страницы.
 *
 * Элемент может вести на маршрут (`to`) или вызывать обработчик (`onClick`) —
 * на карточке машины «Catalog» возвращает в каталог С СОХРАНЁННЫМИ фильтрами,
 * а это не простая ссылка, а переход с состоянием.
 *
 * Файл в `pages/shared/`, поэтому его CSS не инлайнится в Shadow DOM дилера.
 *
 * @param {object} props
 * @param {Array<{label: string, to?: string, onClick?: () => void}>} props.items
 */
export const Breadcrumbs = ({ items, className = '' }) => {
  const showcaseText = useShowcaseText();
  if (!items?.length) return null;

  return (
    <nav
      className={`breadcrumbs${className ? ` ${className}` : ''}`}
      aria-label={showcaseText('showcase.a11y.breadcrumb', 'Breadcrumb')}
    >
      <ol className="breadcrumbs__list">
        {items.map(({ label, to, onClick }, i) => {
          const last = i === items.length - 1;
          return (
            <li key={label} className="breadcrumbs__item">
              {i > 0 && (
                <CaretRight
                  size={12}
                  weight="bold"
                  className="breadcrumbs__sep"
                  aria-hidden="true"
                />
              )}
              {last || (!to && !onClick) ? (
                <span
                  className="breadcrumbs__current"
                  aria-current={last ? 'page' : undefined}
                >
                  {label}
                </span>
              ) : to ? (
                <Link className="breadcrumbs__link" to={to}>
                  {label}
                </Link>
              ) : (
                <button
                  type="button"
                  className="breadcrumbs__link"
                  onClick={onClick}
                >
                  {label}
                </button>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

Breadcrumbs.propTypes = {
  /** Путь целиком, включая текущую страницу последним элементом. */
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      /** Маршрут. Для перехода с состоянием используйте `onClick`. */
      to: PropTypes.string,
      onClick: PropTypes.func,
    })
  ).isRequired,
  className: PropTypes.string,
};

export default Breadcrumbs;
